import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupIcon from '@mui/icons-material/Group';
import InsightsIcon from '@mui/icons-material/Insights';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SavingsIcon from '@mui/icons-material/Savings';
import { useQuery } from '@tanstack/react-query';
import {
  AccountingPeriodSchema,
  AccountSchema,
  CashPositionSchema,
  DepartmentSchema,
  OrganizationSchema,
  PaymentSchema,
  ReferenceMasterSchema,
  SupplierInvoiceSchema,
  SupplierSchema,
  UserSummarySchema,
  type MeResponse,
} from '@taqon/contracts';
import { z } from 'zod';
import { Link as RouterLink } from 'react-router';
import { ApiError, getData, getPage } from '../../shared/api/client';
import { LIVE_QUERY } from '../../shared/ui/live';
import { hasCapability, useSession } from '../../shared/session/session';
import { PageHeader } from '../../shared/ui/page';
import { StatCard, StatGrid } from '../../shared/ui/stats';
import { BRAND, colourCycle } from '../../shared/ui/tokens';
import { useMyTasks } from '../projects/work/api';
import { MyTasksCard } from '../projects/work/MyTasksCard';
import { openableDashboards } from '../reporting/dashboards';
import {
  businessHour,
  ComingUpCard,
  DailyAlert,
  greeting,
  NewsSection,
  useNoticeboard,
  Wishes,
} from './noticeboard';
import { WaitingForYouCard, waitingForYouShown } from './waiting-for-you';

/**
 * The landing page: a summary of what the system holds, built **only** from existing API data.
 *
 * - Counts are the `total` the list endpoints return; the bank and cash figures are the API's own
 *   consolidated cash position, never added up here. Nothing is estimated.
 * - A card appears only when the session may read what it summarises **across the organisation**:
 *   a capability held only through a scoped or own-records grant (`scopedCapabilities`) cannot
 *   give a whole-list count, so its card is not shown (FRD-USR-007, RPT-008).
 * - Every figure is its own link to the list it counts, with the same filter, and its label says
 *   which records it counts ("all statuses" when inactive records are included) (FRD-RPT-007).
 * - Every figure has its own loading and error state: a refusal reads "Not available to you",
 *   anything else "Could not be loaded"; a figure never silently disappears or shows a dash.
 */
export function DashboardPage() {
  const session = useSession();
  const me = session.data;
  if (me === null || me === undefined) return null;
  return <Dashboard me={me} />;
}

function Dashboard({ me }: { me: MeResponse }) {
  const board = useNoticeboard(me);
  // The caller's own assigned tasks (CLR-66), for the card and the morning popup.
  const usesWork = hasCapability(me, 'projects.task:view');
  const myTasks = useMyTasks(usesWork);
  const tasksReady = !usesWork || !myTasks.isPending;
  const can = (capability: string) => wholeOrganisation(me, capability);

  const dashboards = openableDashboards(me);

  // Whole-list counts are the stat cards; the summaries with more to say are the cards below.
  const stats: ReactNode[] = [
    can('ledger.account:view') && (
      <CountCard
        key="accounts"
        title="Chart of accounts"
        icon={<AccountTreeIcon />}
        path="/api/v1/ledger/accounts"
        schema={AccountSchema}
        noun="accounts (all statuses)"
        emptyHint="Import the Chart of Accounts to begin."
        to="/accounts"
      />
    ),
    can('procurement.supplier:view') && (
      <CountCard
        key="suppliers"
        title="Suppliers"
        icon={<LocalShippingIcon />}
        path="/api/v1/procurement/suppliers"
        schema={SupplierSchema}
        noun="suppliers (all statuses)"
        emptyHint="No supplier recorded yet."
        to="/suppliers"
      />
    ),
    can('finance.payment:view') && (
      <CountCard
        key="payments"
        title="Supplier payments"
        icon={<PaymentsIcon />}
        path="/api/v1/finance/payments"
        schema={PaymentSchema}
        noun="payments (all statuses)"
        emptyHint="No payment recorded yet."
        to="/payments"
      />
    ),
    can('platform.app_user:view') && (
      <CountCard
        key="users"
        title="Users"
        icon={<GroupIcon />}
        path="/api/v1/platform/users"
        schema={UserSummarySchema}
        noun="users (all statuses)"
        emptyHint="No user yet."
        to="/admin/users"
      />
    ),
  ].filter(Boolean);

  const cards: ReactNode[] = [
    dashboards.length > 0 && <DashboardsCard key="dashboards" dashboards={dashboards} />,
    can('organization.organization:view') && <OrganizationCard key="org" me={me} />,
    can('ledger.accounting_period:view') && <PeriodsCard key="periods" />,
    can('finance.supplier_invoice:view') && <InvoicesCard key="invoices" />,
    can('finance.bank_cash_account:view') && <BankCashCard key="bank" />,
  ].filter(Boolean);

  return (
    <Stack spacing={3}>
      <PageHeader
        title={`${greeting(businessHour(me.businessTimezone))}, ${me.displayName}`}
        description="What is coming up at TAQON, and an overview of what is recorded in TAQON Infinity."
      />
      <Wishes wishes={board.wishes} />
      {board.shown && (
        // Coming up and News side by side as two equal boxes: the same width, the same height and
        // their headings on one line. Stacked below laptop width, where halves would be too narrow.
        <Grid container spacing={2.5} sx={{ alignItems: 'stretch' }}>
          <Grid size={{ xs: 12, lg: board.news.length > 0 ? 6 : 12 }}>
            <ComingUpCard items={board.items} today={board.today} loading={board.loading} />
          </Grid>
          {board.news.length > 0 && (
            <Grid size={{ xs: 12, lg: 6 }}>
              <NewsSection me={me} news={board.news} />
            </Grid>
          )}
        </Grid>
      )}
      {usesWork && <MyTasksCard tasks={myTasks.data} error={myTasks.error} today={board.today} />}
      <WaitingForYouCard me={me} today={board.today} />
      <DailyAlert
        me={me}
        items={board.items}
        wishes={board.wishes}
        tasks={myTasks.data ?? []}
        today={board.today}
        ready={!board.loading && tasksReady}
      />
      {stats.length > 0 && <StatGrid cols={Math.min(stats.length, 4)}>{stats}</StatGrid>}
      {cards.length === 0 &&
        stats.length === 0 &&
        !board.shown &&
        !usesWork &&
        !waitingForYouShown(me) && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="subtitle1">Nothing to show yet</Typography>
            <Typography variant="body2" color="text.secondary">
              Your access does not include any of the summaries on this page.
            </Typography>
          </Paper>
        )}
      {cards.length > 0 && (
        // Every summary card the same size, on every row: a card alone on the last row is as tall
        // as the others.
        <Box
          sx={{
            display: 'grid',
            gap: 2.5,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            gridAutoRows: '1fr',
            ...colourCycle('& > *'),
          }}
        >
          {cards}
        </Box>
      )}
    </Stack>
  );
}

/**
 * Whether the session may read every record of the capability's type: held, and not only through
 * grants limited to part of the organisation or to the user's own records (UX only; the API
 * enforces, SDD §22.4).
 */
function wholeOrganisation(me: MeResponse, capability: string): boolean {
  return hasCapability(me, capability) && !(me.scopedCapabilities ?? []).includes(capability);
}

/** A summary card: its colour along the top edge and in one corner, and a link to open the area. */
function Card({
  title,
  icon,
  to,
  children,
}: {
  title: string;
  icon: ReactNode;
  /** The page the card opens; a card that lists its own links has none. */
  to?: string;
  children: ReactNode;
}) {
  return (
    <Paper
      component="section"
      aria-label={title}
      sx={{
        p: 2.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        // The card's colour along its top edge and as a soft glow in its corner.
        borderTop: `4px solid var(--cycle-dark, ${BRAND.mint})`,
        backgroundImage: `radial-gradient(220px 160px at 100% 0%, color-mix(in srgb, var(--cycle-light, ${BRAND.mint}) 14%, transparent), transparent 70%)`,
      }}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 2 }}>
        <IconBadge>{icon}</IconBadge>
        <Typography variant="subtitle1" component="h2">
          {title}
        </Typography>
      </Stack>
      <Box sx={{ flexGrow: 1 }}>{children}</Box>
      {to !== undefined && (
        <Box sx={{ mt: 2 }}>
          <Button
            component={RouterLink}
            to={to}
            size="small"
            sx={{ px: 0, color: `var(--cycle-dark, ${BRAND.ink})` }}
          >
            Open {title.toLowerCase()}
          </Button>
        </Box>
      )}
    </Paper>
  );
}

function IconBadge({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 2,
        display: 'grid',
        placeItems: 'center',
        bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
        color: `var(--cycle-icon, ${BRAND.ink})`,
        backgroundImage:
          'linear-gradient(135deg, var(--cycle-light, transparent), var(--cycle-dark, transparent))',
        boxShadow: '0 8px 16px -8px var(--cycle-dark, transparent)',
      }}
    >
      {children}
    </Box>
  );
}

/** Why a figure could not be shown, in words; never the raw failure alone. */
function unavailableText(error: Error): string {
  if (error instanceof ApiError && error.status === 403) return 'Not available to you';
  return `Could not be loaded: ${error.message}`;
}

/** What a figure needs of its read: a query result, or one derived from it. */
interface FigureState {
  readonly isPending: boolean;
  readonly error: Error | null;
  readonly data: number | undefined;
}

/**
 * One figure: its value as a link to the list it counts, and its label. While loading, a
 * placeholder; on failure, the figure's own message in its place.
 */
function Figure({
  query,
  label,
  to,
  size = 'large',
}: {
  query: FigureState;
  label: string;
  to: string;
  size?: 'large' | 'small';
}) {
  const valueSx =
    size === 'large'
      ? { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.2 }
      : { fontWeight: 700 };
  return (
    <Box>
      {query.isPending && (
        <Skeleton
          variant="text"
          width={size === 'large' ? 120 : 40}
          height={size === 'large' ? 40 : 24}
        />
      )}
      {query.error !== null && (
        <Typography variant="body2" color="text.secondary" role="status">
          {unavailableText(query.error)}
        </Typography>
      )}
      {query.data !== undefined && (
        <Link
          component={RouterLink}
          to={to}
          underline="hover"
          color="inherit"
          aria-label={`${String(query.data)} ${label}`}
          sx={{ display: 'inline-block', ...valueSx }}
        >
          {query.data}
        </Link>
      )}
      <Typography
        variant={size === 'large' ? 'body2' : 'caption'}
        color="text.secondary"
        sx={{ display: 'block' }}
      >
        {label}
      </Typography>
    </Box>
  );
}

/** A row count: the `total` the list endpoint reports for a one-row page. */
function useTotal(path: string, schema: z.ZodType, enabled = true) {
  return useQuery({
    queryKey: ['dashboard', 'total', path],
    enabled,
    retry: false,
    queryFn: async () =>
      (await getPage(`${path}${path.includes('?') ? '&' : '?'}pageSize=1`, schema)).total,
    ...LIVE_QUERY,
  });
}

/** A whole-list count as a stat card: the figure links to the list it counts. */
function CountCard({
  title,
  icon,
  path,
  schema,
  noun,
  emptyHint,
  to,
}: {
  title: string;
  icon: ReactNode;
  path: string;
  schema: z.ZodType;
  noun: string;
  emptyHint: string;
  to: string;
}) {
  const total = useTotal(path, schema);
  return (
    <Box component="section" aria-label={title}>
      <StatCard
        // An empty list says what to do next; the figure's link still names what it counts.
        label={total.data === 0 ? emptyHint : noun}
        icon={icon}
        value={
          <>
            {total.isPending && <Skeleton variant="text" width={64} />}
            {total.error !== null && (
              <Typography variant="body2" color="text.secondary" role="status" noWrap>
                {unavailableText(total.error)}
              </Typography>
            )}
            {total.data !== undefined && (
              <Link
                component={RouterLink}
                to={to}
                underline="hover"
                color="inherit"
                aria-label={`${String(total.data)} ${noun}`}
              >
                {total.data}
              </Link>
            )}
          </>
        }
      />
    </Box>
  );
}

/** The Release 1 dashboards the session may open (FRD-RPT-002), one link each. */
/** The dashboards the session may open: the same frame as the other cards, its own links inside. */
function DashboardsCard({ dashboards }: { dashboards: ReturnType<typeof openableDashboards> }) {
  return (
    <Card title="Dashboards" icon={<InsightsIcon />}>
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {dashboards.map((dashboard) => (
          <li key={dashboard.code}>
            <Button
              component={RouterLink}
              to={`/dashboards/${dashboard.code}`}
              size="small"
              sx={{ px: 0, color: `var(--cycle-dark, ${BRAND.ink})` }}
            >
              {dashboard.title}
            </Button>
          </li>
        ))}
      </Stack>
    </Card>
  );
}

/** Legal entity and the organization masters; each count shown only with its own permission. */
function OrganizationCard({ me }: { me: MeResponse }) {
  const organization = useQuery({
    queryKey: ['dashboard', 'organization'],
    retry: false,
    queryFn: () => getData('/api/v1/organization/organization', OrganizationSchema),
  });
  const masters = [
    {
      label: 'departments (all statuses)',
      capability: 'organization.department:view',
      path: '/api/v1/organization/departments',
      schema: DepartmentSchema,
      to: '/organization/departments',
    },
    {
      label: 'cost centres (all statuses)',
      capability: 'organization.cost_center:view',
      path: '/api/v1/organization/cost-centers',
      schema: ReferenceMasterSchema,
      to: '/organization/cost-centres',
    },
    {
      label: 'locations (all statuses)',
      capability: 'organization.location:view',
      path: '/api/v1/organization/locations',
      schema: ReferenceMasterSchema,
      to: '/organization/locations',
    },
  ].filter((master) => wholeOrganisation(me, master.capability));
  return (
    <Card title="Organization" icon={<BusinessIcon />} to="/organization">
      {organization.isPending && <Skeleton variant="text" width={160} height={32} />}
      {organization.isError && (
        <Typography variant="body2" color="text.secondary" role="status">
          Legal entity: {unavailableText(organization.error)}
        </Typography>
      )}
      {organization.data !== undefined && (
        <>
          <Typography sx={{ fontWeight: 600 }}>{organization.data.legalName}</Typography>
          <Typography variant="body2" color="text.secondary">
            {organization.data.registeredCity}, {organization.data.registeredCountry}
          </Typography>
        </>
      )}
      {masters.length > 0 && (
        <Stack direction="row" spacing={3} useFlexGap sx={{ mt: 2, flexWrap: 'wrap' }}>
          {masters.map((master) => (
            <MasterFigure key={master.path} {...master} />
          ))}
        </Stack>
      )}
    </Card>
  );
}

function MasterFigure({
  label,
  path,
  schema,
  to,
}: {
  label: string;
  path: string;
  schema: z.ZodType;
  to: string;
}) {
  const total = useTotal(path, schema);
  return <Figure query={total} label={label} to={to} size="small" />;
}

function PeriodsCard() {
  const periods = useQuery({
    queryKey: ['dashboard', 'accounting-periods'],
    retry: false,
    queryFn: () => getData('/api/v1/ledger/accounting-periods', z.array(AccountingPeriodSchema)),
    ...LIVE_QUERY,
  });
  const open = (periods.data ?? []).filter((period) => period.status === 'open');
  // The count of open periods, as a figure of its own (the periods list shows each one's status).
  const count: FigureState = {
    isPending: periods.isPending,
    error: periods.error,
    data: periods.data === undefined ? undefined : open.length,
  };
  return (
    <Card title="Accounting periods" icon={<CalendarMonthIcon />} to="/periods">
      <Figure
        query={count}
        label={open.length === 1 ? 'open period' : 'open periods'}
        to="/periods"
      />
      {open.slice(0, 3).map((period) => (
        <Typography key={period.id} variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {period.code}: {period.startDate} to {period.endDate}
        </Typography>
      ))}
    </Card>
  );
}

/** Supplier invoices: all, drafts and posted, each linking to the list with that filter. */
function InvoicesCard() {
  const path = '/api/v1/finance/supplier-invoices';
  const all = useTotal(path, SupplierInvoiceSchema);
  const drafts = useTotal(`${path}?filter[status]=draft`, SupplierInvoiceSchema);
  const posted = useTotal(`${path}?filter[status]=posted`, SupplierInvoiceSchema);
  return (
    <Card title="Supplier invoices" icon={<ReceiptLongIcon />} to="/supplier-invoices">
      <Figure query={all} label="supplier invoices (all statuses)" to="/supplier-invoices" />
      {all.data === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No supplier invoice recorded yet.
        </Typography>
      ) : (
        <Stack direction="row" spacing={3} sx={{ mt: 1.5 }}>
          <Figure query={drafts} label="Draft" to="/supplier-invoices?status=draft" size="small" />
          <Figure
            query={posted}
            label="Posted"
            to="/supplier-invoices?status=posted"
            size="small"
          />
        </Stack>
      )}
    </Card>
  );
}

function BankCashCard() {
  // The consolidated cash position (FRD-FIN-093; BRD §12 Finance dashboard: cash) exactly as the
  // API derives it from posted movements — the total and each register — never summed here.
  const position = useQuery({
    queryKey: ['dashboard', 'cash-position'],
    retry: false,
    queryFn: () => getData('/api/v1/finance/cash-position', CashPositionSchema),
    ...LIVE_QUERY,
  });
  const registers = position.data?.registers ?? [];
  return (
    <Card title="Bank & cash" icon={<SavingsIcon />} to="/bank-cash">
      {position.isPending && <Skeleton variant="text" width={120} height={40} />}
      {position.isError && (
        <Typography variant="body2" color="text.secondary" role="status">
          {unavailableText(position.error)}
        </Typography>
      )}
      {position.data !== undefined && registers.length === 0 && (
        <>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.2 }}>0</Typography>
          <Typography variant="body2" color="text.secondary">
            registers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No bank or cash register configured yet.
          </Typography>
        </>
      )}
      {position.data !== undefined && registers.length > 0 && (
        <Stack spacing={1.25}>
          <Box>
            <Link
              component={RouterLink}
              to="/bank-cash"
              underline="hover"
              color="inherit"
              sx={{
                display: 'inline-block',
                fontWeight: 700,
                fontSize: '1.5rem',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              AED {position.data.total}
            </Link>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Cash position across {registers.length} register(s)
            </Typography>
          </Box>
          {registers.slice(0, 4).map((register) => (
            <Stack
              key={register.id}
              direction="row"
              spacing={2}
              sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}
            >
              <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                {register.name}
              </Typography>
              <Typography
                sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
              >
                AED {register.balance}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Card>
  );
}
